import * as TWEEN from '@tweenjs/tween.js'
import * as THREE from 'three'
import { MapControls } from 'three/examples/jsm/controls/MapControls'

enum CameraMode {
  MapView,
  StreetView,
  PoiView,
}

enum TweenMode {
  BackToMapView,
  ClickOnPOI,
  ClickOnTerrain,
}

export type MiniMapProps = {
  position: number[]
  dir: number[]
}

type IntersectionTrace = THREE.Intersection<THREE.Object3D<THREE.Event>>

interface IThreeBurApp {
  getHeightAtPosition: (pos: THREE.Vector3) => number
  updateMinimap: ({ position, dir }: MiniMapProps) => void
  updatePOIMode: (enabled: boolean) => void
}

function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180.0)
}

/**
 * Returns a vector in the direction of A->B of length x.
 * @param A origin vector
 * @param B target vector
 * @param x desired length
 */
function getScaledVector(A: THREE.Vector3, B: THREE.Vector3, x: number): THREE.Vector3 {
  const v = new THREE.Vector3(0, 0, 0)
  v.subVectors(B, A)
  v.normalize()
  return v.multiplyScalar(x)
}

/**
 * A CameraController packs away all stuff related to camera and camera controls.
 * This includes camera mode, panning, zooming, orbiting, rotation, and certain
 * types of events that are sensitive to which camera mode should be currently
 * activated.
 */
class CameraController {
  // This field controls which mode the camera is in, which affects how
  // it should handle input events.
  private cameraMode: CameraMode = CameraMode.MapView

  // Underlying camera object, used by `controls_` (defined further down)
  private camera_: THREE.PerspectiveCamera

  private origin_: THREE.Vector2

  // Map controls are used for panning and orbiting the camera
  private controls_: MapControls

  // Used for signalling a new rendering frame because camera was changed
  private shouldRender_: boolean

  // Imploded circular reference hack because we need it (best option available)
  private app_: IThreeBurApp

  // When we fly to a POI, this is the desired horizontal distance to the camera
  private flyToPOIDist_ = 30

  // When we fly out to map view, this is the desired horizontal distance to the camera
  private flyToMapViewDist_ = 50

  // Extent (bounding box) of the world scene, used to clamp the camera position,
  // and a temporary storage to prevent creating a new vector every frame.
  private sceneExtent_: THREE.Box3 | undefined
  private clampedPosition_: THREE.Vector3

  // Auto-rotate in POI view
  private idleTimeToAutoRotateMs_ = 15000
  private lastTime_ = Date.now()
  private enableIdleRotation(): void {
    this.controls_.autoRotate = true
    this.controls_.autoRotateSpeed = 0.7 // default is 2
  }
  private disableIdleRotation(): void {
    this.controls_.autoRotate = false
  }

  // Restrict camera and orbit target to be above the scene and inside the scene extent.
  private restrictCameraMovement(): void {
    // Minimum eye height at 1.6m
    const heightAtCamera = this.app_.getHeightAtPosition(this.camera_.position)
    const minEyeHeight = 1.6
    if (heightAtCamera > this.camera_.position.y - minEyeHeight) {
      this.camera_.position.y = heightAtCamera + minEyeHeight
      this.camera_.updateMatrix()
    }

    // Boundary clamp inside loaded scene
    if (this.sceneExtent_) {
      const height = this.controls_.target.y // we do not modify the height value
      this.sceneExtent_.clampPoint(this.controls_.target, this.clampedPosition_)
      this.controls_.target.copy(this.clampedPosition_)
      this.controls_.target.y = height
    }
  }

  private setViewMap(): void {
    this.disableIdleRotation() // no idle rotation allowed in map view

    this.controls_.minDistance = 50
    this.controls_.maxDistance = 750
    this.controls_.minPolarAngle = degreesToRadians(10.0)
    this.controls_.maxPolarAngle = degreesToRadians(50.0)

    this.controls_.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    }

    this.controls_.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_ROTATE,
    }

    this.cameraMode = CameraMode.MapView
  }

  private setViewPOI(): void {
    this.controls_.minDistance = 10
    this.controls_.maxDistance = 75
    this.controls_.minPolarAngle = degreesToRadians(10.0)
    this.controls_.maxPolarAngle = degreesToRadians(80.0)

    this.controls_.mouseButtons = {
      LEFT: undefined,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    }
    this.controls_.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_ROTATE,
    }

    this.cameraMode = CameraMode.PoiView
  }

  public calcCameraPositionAndDirection(): MiniMapProps {
    const cameraDir = new THREE.Vector3()
    // Handle camera movement and apply callback
    const position = [this.camera_.position.x + this.origin_.x, this.origin_.y - this.camera_.position.z]
    this.camera_.getWorldDirection(cameraDir)
    const projectedlength = Math.sqrt(cameraDir.x * cameraDir.x + cameraDir.z * cameraDir.z)
    const dir = [cameraDir.x, -cameraDir.z]
    if (projectedlength > 0.0) {
      dir[0] /= projectedlength
      dir[1] /= projectedlength
    }

    return { position, dir }
  }

  private doTween(newTarget: THREE.Vector3, newCamPos: THREE.Vector3, tweenMode: TweenMode): void {
    // Tweening parameters
    let tweenTimeOrientMs = 1700
    let tweenTimeMoveMs = 1700
    let orientEasing = TWEEN.Easing.Quartic.InOut
    let moveEasing = TWEEN.Easing.Quartic.InOut

    switch (tweenMode) {
      case TweenMode.BackToMapView:
        tweenTimeOrientMs = 1260
        tweenTimeMoveMs = 1260
        orientEasing = TWEEN.Easing.Quadratic.InOut
        moveEasing = TWEEN.Easing.Quadratic.InOut
        break
      case TweenMode.ClickOnPOI:
        break
      case TweenMode.ClickOnTerrain:
        // For shorter distances, using shorter out-only tweening works better
        if (newTarget.distanceToSquared(this.camera_.position) < 7600) {
          tweenTimeOrientMs = 1050
          tweenTimeMoveMs = 1050
          orientEasing = TWEEN.Easing.Quartic.Out
          moveEasing = TWEEN.Easing.Quartic.Out
        } else {
          tweenTimeOrientMs = 1500
          tweenTimeMoveMs = 1500
        }
        break
      default:
        break
    }

    // construct target camera
    const targetCamera = this.camera_.clone()
    targetCamera.position.copy(newCamPos)
    targetCamera.lookAt(newTarget)

    // 1) Tween to re-orient the camera
    const oldOrientation = this.camera_.quaternion.clone()
    const newOrientation = targetCamera.quaternion
    const t0 = { t: 0 }
    const t1 = { t: 1 }
    const orientTween = new TWEEN.Tween(t0).to(t1, tweenTimeOrientMs)
    orientTween.onUpdate(() => this.camera_.quaternion.slerpQuaternions(oldOrientation, newOrientation, t0.t))
    orientTween.easing(orientEasing)
    orientTween.start()

    // 2) Tween to move the camera
    const moveTween = new TWEEN.Tween(this.camera_.position).to(newCamPos, tweenTimeMoveMs)
    moveTween.easing(moveEasing)
    moveTween.start()

    // New controls target after tween has completed
    this.controls_.target.copy(newTarget)
  }

  /**
   * Here we construct a camera controller.
   * @param canvas HTML5 canvas
   * @param cameraPos Initial camera position
   * @param lookAtPos Initial lookAt target position, together with cameraPos
   * determines the camera's initial orientation.
   * @param getHeightAtPositionFn Callback function which intersects a position
   * with the 3D scene and returns a height value at that position.
   */
  constructor(canvas: HTMLCanvasElement, cameraPos: THREE.Vector3, lookAtPos: THREE.Vector3, burApp: IThreeBurApp) {
    this.origin_ = new THREE.Vector2(0, 0)
    this.clampedPosition_ = new THREE.Vector3(0, 0, 0)

    // Hardcode initial camera
    const aspect = canvas.width / canvas.height
    this.camera_ = new THREE.PerspectiveCamera(45, aspect, 1, 1500)
    this.camera_.position.copy(cameraPos)
    this.camera_.quaternion.set(1, 0, 0, 0)
    this.camera_.zoom = 1 // default is 1

    // Default map controls
    this.controls_ = new MapControls(this.camera_, canvas)
    this.controls_.position0.copy(cameraPos)
    this.controls_.target0.copy(lookAtPos)
    this.controls_.target.copy(lookAtPos) // gets reset during rendering
    this.controls_.enableDamping = true
    this.controls_.dampingFactor = 0.15 // default is 0.05

    this.controls_.screenSpacePanning = false
    this.controls_.keyPanSpeed = 0 // set to 0 to disallow keyboard arrow keys to pan the camera
    this.controls_.enableZoom = true // default is true
    this.controls_.zoomSpeed = 3 // default is 1
    this.setViewMap()
    this.controls_.listenToKeyEvents(window)
    this.controls_.update()

    this.shouldRender_ = false
    this.app_ = burApp

    // Fires when the camera has been transformed by the controls
    this.controls_.addEventListener('change', () => {
      this.shouldRender_ = true
      this.lastTime_ = Date.now()
      this.restrictCameraMovement()
    })

    // Fires when an interaction was initiated
    this.controls_.addEventListener('start', () => {
      this.disableIdleRotation()
    })
  }

  public updateCameraIdleTimer(): void {
    if (this.cameraMode == CameraMode.MapView) {
      return
    }

    const nowTime = Date.now()
    const elapsed = nowTime - this.lastTime_
    if (elapsed > this.idleTimeToAutoRotateMs_) {
      this.enableIdleRotation()
    }
  }

  public SetSceneExtent(extent: THREE.Box3): void {
    this.sceneExtent_ = extent.clone()
  }

  // An input event was triggered which clicked on a Point-of-interest.
  // CameraController will handle the details.
  public OnClickedPOI(markerPos: THREE.Vector3): void {
    this.controls_.stopListenToKeyEvents()
    this.lastTime_ = Date.now()
    const initialMapView = this.cameraMode == CameraMode.MapView
    if (this.cameraMode != CameraMode.PoiView) {
      this.setViewPOI()
    }

    const newCamPos = markerPos.clone()
    newCamPos.sub(getScaledVector(this.camera_.position, markerPos, this.flyToPOIDist_))

    // Here, we determine a height offset so camera altitude will be higher than the POI's.
    // This will eliminate a "jump" in the camera view after the tweening is performed,
    // because the camera's tweening direction was outside the min/max allowed polar angle
    // range of the MapControls object.
    const heightDiff = Math.abs(this.camera_.position.y - markerPos.y)
    if (markerPos.y > this.camera_.position.y) {
      // tweening upwards
      newCamPos.y += heightDiff
    } else if (initialMapView) {
      // tweening downwards from map view, so we adjust the angle so the camera is facing
      // the POI from a more horizontal viewing direction.
      newCamPos.y = markerPos.y
    }
    newCamPos.y += 7 // Always add default offset

    this.doTween(markerPos, newCamPos, TweenMode.ClickOnPOI)
    this.controls_.listenToKeyEvents(window)
  }

  // An input event was triggered which clicked on a general position, ie. not a Point-of-interest.
  public OnClickedPosition(intersection: IntersectionTrace): void {
    this.controls_.stopListenToKeyEvents()
    this.lastTime_ = Date.now()
    let keepHeight = true
    if (this.cameraMode == CameraMode.MapView) {
      this.app_.updatePOIMode(true)
      this.setViewPOI()
      keepHeight = false
    }

    // First we check the normal vector of the clicked surface, as it will effect how the
    // final camera orientation should be.
    const offset = new THREE.Vector3()
    const distance = 40
    if (intersection.normal && Math.abs(intersection.normal.y) < 0.9) {
      offset.setX(distance * intersection.normal.x)
      offset.setY(distance * 0.25)
      offset.setZ(distance * intersection.normal.z)
    } else {
      // Ground point
      offset.subVectors(this.camera_.position, intersection.point).setY(0).normalize()
      offset.setX(offset.x * distance)
      offset.setY(distance * 0.5)
      offset.setZ(offset.z * distance)
    }

    // Compute new camera position and make sure it's always above the landscape.
    // NOTE: If we were already in POI view, then we wish to keep the existing camera height.
    const newCamPos = intersection.point.clone().add(offset)
    if (keepHeight) {
      newCamPos.y = this.camera_.position.y
    } else {
      newCamPos.y += 2 // offset to always be above the landscape
    }

    this.doTween(intersection.point, newCamPos, TweenMode.ClickOnTerrain)
    this.controls_.listenToKeyEvents(window)
  }

  public OnClickedExitPOIButton(): void {
    if (this.cameraMode == CameraMode.MapView) {
      return
    }
    this.controls_.stopListenToKeyEvents()
    this.disableIdleRotation()

    // Compute new controls target and camera position, and tween thereto
    const newTarget = this.controls_.target.clone()
    newTarget.y = 0
    const newCamPos = this.camera_.position.clone()
    newCamPos.y = 0
    const offset2 = getScaledVector(newTarget, newCamPos, this.flyToMapViewDist_)
    newCamPos.copy(newTarget)
    newCamPos.add(offset2)
    newCamPos.y = this.controls_.position0.y // jump to initial height
    this.doTween(newTarget, newCamPos, TweenMode.BackToMapView)

    this.setViewMap()
    this.controls_.listenToKeyEvents(window)
  }

  // There is a button, on mobile platforms only, that when clicked should transfer
  // the user to their current GPS location.
  public OnClickedGotoGeoPosition(position: THREE.Vector3): void {
    this.controls_.stopListenToKeyEvents()
    this.app_.updatePOIMode(false)

    const newCamPos = position.clone()
    newCamPos.sub(getScaledVector(this.camera_.position, position, this.flyToMapViewDist_ + 25))
    // The magic number 238.484800354 is computed from Pythagoras, as we want a camera
    // position completely zoomed out, and max zoom of controls object is 250, and
    // horizontal distance is this.flyToMapViewDist_ = 50. We also add a horizontal
    // offset (= 25) since the camera is zoomed out, so the formula becomes:
    // h = sqrt(250*250 - 75*75).
    newCamPos.y = position.y + 238.484800354

    this.doTween(position, newCamPos, TweenMode.BackToMapView)

    this.setViewMap()
    this.controls_.listenToKeyEvents(window)
  }

  public OnFlyToAssetFromUrl(position: THREE.Vector3): void {
    this.lastTime_ = Date.now()
    this.disableIdleRotation()

    this.OnClickedPOI(position)
    this.app_.updatePOIMode(true)
  }

  public FlyToPosition(height: number, x: number, y: number, useTween: boolean, tweenDurationMs: number): void {
    this.controls_.stopListenToKeyEvents()
    this.disableIdleRotation()

    const viewHeight = height + 30
    const viewDistance = 200
    const targetPos = new THREE.Vector3(x, height, y)
    const dirToTarget = targetPos.clone().sub(this.camera_.position)
    dirToTarget.setY(0)
    dirToTarget.normalize().multiplyScalar(viewDistance)

    const targetCamera = this.camera_.clone()
    targetCamera.position.set(x - dirToTarget.x, viewHeight, y - dirToTarget.z)
    targetCamera.lookAt(targetPos)

    if (useTween) {
      // use easing, then quaternion slerp easing
      new TWEEN.Tween(this.camera_.position).to(targetCamera.position, tweenDurationMs).easing(TWEEN.Easing.Quartic.Out).start()
      const tweenobj = { t: 0 }
      const oldQuat = this.camera_.quaternion.clone()
      new TWEEN.Tween(tweenobj)
        .to({ t: 1 }, tweenDurationMs)
        .onUpdate(() => {
          this.camera_.quaternion.slerpQuaternions(oldQuat, targetCamera.quaternion, tweenobj.t)
        })
        .easing(TWEEN.Easing.Quartic.Out)
        .start()
    } else {
      // don't use easing
      this.camera_.copy(targetCamera)
    }

    this.controls_.target.copy(targetPos)
    if (this.cameraMode == CameraMode.MapView) {
      this.setViewPOI()
    }

    this.controls_.listenToKeyEvents(window)
  }

  // Needed for rendering
  public GetCamera(): THREE.Camera {
    return this.camera_
  }

  public GetCameraMode(): CameraMode {
    return this.cameraMode
  }

  public GetControls(): MapControls {
    return this.controls_
  }

  public SetOrigin(x: number, y: number) {
    this.origin_.x = x
    this.origin_.y = y
  }

  public GetOrigin(): THREE.Vector2 {
    return this.origin_
  }

  public GetCameraPosition(): THREE.Vector3 {
    return this.camera_.position
  }

  public SetTarget(newTarget: THREE.Vector3): void {
    this.controls_.target.x = newTarget.x
    this.controls_.target.y = newTarget.y
    this.controls_.target.z = newTarget.z
  }

  public EnableLayer(layer: number): void {
    this.camera_.layers.enable(layer)
  }

  public UpdateViewportSize(w: number, h: number): void {
    this.camera_.aspect = w / h
    this.camera_.updateProjectionMatrix()
    this.shouldRender_ = true
  }

  public ResetCameraView(sceneAabb: THREE.Box3): void {
    const size = new THREE.Vector3()
    sceneAabb.getSize(size)

    // Jump back to start position and set camera mode to map view
    this.camera_.position.copy(this.controls_.position0)
    this.controls_.target.copy(this.controls_.target0)
    this.setViewMap()

    this.camera_.far = Math.max(3000, 2 * Math.max(size.x, size.y, size.z))
    this.camera_.near = 0.001 * this.camera_.far
    this.camera_.updateProjectionMatrix()

    this.controls_.update()
    this.shouldRender_ = true
  }

  public IsOriginZero(): boolean {
    return this.origin_.x == 0.0 && this.origin_.y == 0.0
  }

  public ShouldRender(): boolean {
    const ret: boolean = this.shouldRender_
    this.shouldRender_ = false // called every frame, reset is safe
    return this.controls_.update() || ret
  }
}

export { CameraController }
export type { IThreeBurApp }
